/**
 * Ported from the-answer-2 (BACKLOG §16: lift the components, not the app).
 * Behaviour is theirs; the edges are savvy's. Changes made on the way in:
 *   - the field's identity is a stable string `id`, not a client-assigned
 *     `callTypeFormFieldId` number sitting beside an unrelated `id`;
 *   - a section is a flat `group` name, not a nested object with its own id;
 *   - drafts, feature announcements, router state and toasts are dropped —
 *     they belong to the host app, not the component.
 */


import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { GripVertical } from "lucide-react";

interface SortableGroupProps {
  groupId: string;
  children: React.ReactNode;
  isDraggingField?: boolean;
}

// Color palette for group visual distinction
const GROUP_COLORS = [
  {
    border: "border-primary/40",
    borderHover: "border-primary/60",
    bg: "bg-primary/5",
    bgHover: "bg-primary/10",
    ring: "ring-primary/20",
    text: "text-primary",
    indicator: "bg-primary",
    indicatorShadow: "shadow-primary/50",
  },
  {
    border: "border-purple-400/40",
    borderHover: "border-purple-500/60",
    bg: "bg-purple-500/5",
    bgHover: "bg-purple-500/10",
    ring: "ring-purple-500/20",
    text: "text-purple-600",
    indicator: "bg-purple-500",
    indicatorShadow: "shadow-purple-500/50",
  },
  {
    border: "border-emerald-400/40",
    borderHover: "border-emerald-500/60",
    bg: "bg-emerald-500/5",
    bgHover: "bg-emerald-500/10",
    ring: "ring-emerald-500/20",
    text: "text-emerald-600",
    indicator: "bg-emerald-500",
    indicatorShadow: "shadow-emerald-500/50",
  },
  {
    border: "border-orange-400/40",
    borderHover: "border-orange-500/60",
    bg: "bg-orange-500/5",
    bgHover: "bg-orange-500/10",
    ring: "ring-orange-500/20",
    text: "text-orange-600",
    indicator: "bg-orange-500",
    indicatorShadow: "shadow-orange-500/50",
  },
  {
    border: "border-pink-400/40",
    borderHover: "border-pink-500/60",
    bg: "bg-pink-500/5",
    bgHover: "bg-pink-500/10",
    ring: "ring-pink-500/20",
    text: "text-pink-600",
    indicator: "bg-pink-500",
    indicatorShadow: "shadow-pink-500/50",
  },
  {
    border: "border-blue-400/40",
    borderHover: "border-blue-500/60",
    bg: "bg-blue-500/5",
    bgHover: "bg-blue-500/10",
    ring: "ring-blue-500/20",
    text: "text-blue-600",
    indicator: "bg-blue-500",
    indicatorShadow: "shadow-blue-500/50",
  },
];

// A stable colour per section name, so a group keeps its colour across
// reorders. Upstream keyed off a numeric group id parsed out of the DOM id;
// sections are named here, so the name is hashed instead.
function getGroupColor(groupId: string): (typeof GROUP_COLORS)[number] {
  let hash = 0;
  for (let i = 0; i < groupId.length; i++) {
    hash = (hash * 31 + groupId.charCodeAt(i)) | 0;
  }
  return GROUP_COLORS[Math.abs(hash) % GROUP_COLORS.length]!;
}

export function SortableGroup({
  groupId,
  children,
  isDraggingField,
}: SortableGroupProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: groupId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const colors = getGroupColor(groupId);

  return (
    <div className="relative">
      {/* Drop indicator */}
      {isOver && !isDragging && (
        <div
          className={cn(
            "absolute -top-1 left-0 right-0 h-0.5 rounded-full shadow-lg z-20",
            colors.indicator,
            colors.indicatorShadow
          )}
        />
      )}

      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-3 space-y-3 transition-all",
          colors.border,
          colors.bg,
          {
            "opacity-40": isDragging,
            [colors.borderHover]: isOver && !isDragging,
            [colors.bgHover]: isOver && !isDragging,
            "shadow-lg ring-2": isOver && !isDragging,
            [colors.ring]: isOver && !isDragging,
            "opacity-50": isDraggingField,
          }
        )}
      >
        <div className="absolute -top-2.5 left-3 px-2 bg-background flex items-center gap-1.5">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:bg-accent rounded p-0.5 -ml-1 active:cursor-grabbing"
            aria-label="Drag group to reorder"
          >
            <GripVertical className={cn("h-3 w-3", colors.text)} />
          </div>
          <span className={cn("text-xs font-medium", colors.text)}>
            Field Group
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
